import {
  Tray,
  PaginationData,
  TrayInventoryEntry,
} from 'constants/interface';
import { InventoryEntryTypeEnum, InventoryTypeEnum } from 'constants/inventory';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateTrayData extends Omit<
  Tray,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy' |
  'resources'
> {
  resources: {
    resourceId: string
    resourceConsumption: number
  }[]
}

export interface UpdateTrayData {
  id: string
  data: CreateTrayData
}

// TRAY ENTRIES

export interface GetTrayEntriesList extends PaginationData {
  type?: InventoryEntryTypeEnum
  inventoryType?: InventoryTypeEnum
  organization?: string
}

export interface TrayApi {
  createTray(trayData: CreateTrayData): Promise<Tray>
  updateTray(trayData: UpdateTrayData): Promise<Tray>
  getTray(trayId: string): Promise<Tray>
  getTrayList(filter: PaginationData): Promise<PaginatedResponse<Tray>>

  getTrayEntriesList(filter: PaginationData): Promise<PaginatedResponse<TrayInventoryEntry>>
}

const trayApiBuilder = (): TrayApi => {
  const createTray = async (
    trayData: CreateTrayData,
  ): Promise<Tray> => {
    const params: Record<string, unknown> = {
      ...trayData,
    };
    const result = await requestClient.post('/trays', params);

    const createdTray: Tray = result.data as Tray;
    return createdTray;
  };

  const updateTray = async (
    trayData: UpdateTrayData,
  ): Promise<Tray> => {
    const { id: trayId, data } = trayData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/trays/${trayId}`, params);
    const updatedTray: Tray = result.data as Tray;
    return updatedTray;
  };

  const getTray = async (
    trayId: string,
  ): Promise<Tray> => {
    const result = await requestClient.get(`/trays/${trayId}`);
    const tray: Tray = result.data as Tray;
    return tray;
  };

  const getTrayList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<Tray>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/trays', params);
    const pagination = getPaginationHeaders(result);
    const trayList: Tray[] = result.data as Tray[];
    return {
      pagination,
      data: trayList,
    };
  };

  // TRAY ENTRIES

  const getTrayEntriesList = async (
    filterData: GetTrayEntriesList,
  ): Promise<PaginatedResponse<TrayInventoryEntry>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/trays/entries', params);
    const pagination = getPaginationHeaders(result);
    const trayEntriesList: TrayInventoryEntry[] = result.data as TrayInventoryEntry[];
    return {
      pagination,
      data: trayEntriesList,
    };
  };

  return {
    createTray,
    updateTray,
    getTray,
    getTrayList,

    getTrayEntriesList,
  };
};

export const trayApi = trayApiBuilder();
